import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';

import AxisBoundaryHeader, { AXIS_LABEL_SX, AxisBoundaryHeaderProps } from './AxisBoundaryHeader';
import { axisGapDuration } from './bookingsAxis';

interface AxisGapHeaderProps extends AxisBoundaryHeaderProps {
  /** Days the compressed span skips — the same count passed to TimelinePeriodCell's break cell. */
  days: number;
  /** False only on the narrow leading edge break, where even the caption alone would not fit. */
  showCaption?: boolean;
}

// The caption sits at the column's MIDPOINT while the date (when there is one) centers on the
// column's left edge, so the two never contend for the same space: at BREAK_COLUMN_WIDTH a full
// `DD MMM YY` reaches ~34px either side of the edge and the caption starts ~61px in. No edge tick
// either (Round 19: headers carry no vertical lines) — the band's own torn edges in the rows mark
// the cut.
const CAPTION_SX = {
  position: 'absolute' as const,
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  ...AXIS_LABEL_SX,
};

/**
 * A compressed stretch's header: how long it skips, plus — at its left edge — the date the
 * booking before it ended, so the viewer can read which period the gap covers rather than only
 * its duration. The date is delegated to AxisBoundaryHeader untouched: it is an ordinary axis
 * boundary label, resolved through the same collision ladder as every other one, and reusing that
 * component keeps its hover/unmount lifecycle in a single place.
 *
 * The gap's own CLOSING date is not this header's business — the anchor that resumes right after
 * the band carries it, and a trailing gap deliberately has none.
 */
const AxisGapHeader = ({ days, showCaption = true, ...labelProps }: AxisGapHeaderProps) => {
  const { t } = useTranslation();
  const { unit, count } = axisGapDuration(days);
  const caption =
    unit === 'months'
      ? t('timeline_axis_gap_months', { count })
      : t('timeline_axis_gap_days', { count });

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      {showCaption && (
        <Box data-testid="axis-gap-caption" sx={CAPTION_SX}>
          {caption}
        </Box>
      )}
      <AxisBoundaryHeader {...labelProps} />
    </Box>
  );
};

export default AxisGapHeader;
